import dayjs from "dayjs";

export const formatCurrency = (value, currency) => {
  if (isNaN(value) || !currency) return value;

  // Create our number formatter.
  const formatter = new Intl.NumberFormat(
    navigator.language.includes("en") ? "en-US" : navigator.language,
    {
      style: "currency",
      currency,
      // trailingZeroDisplay: "stripIfInteger",
      // It will only stop printing
      // the fraction when the input
      // amount is a round number (int)
      // already.
    }
  );

  return formatter.format(value);
};

export const formatDateRange = (value) => {
  if (
    !value ||
    !Array.isArray(value) ||
    value.length !== 2 ||
    !(value[0] instanceof Date) ||
    !(value[1] instanceof Date)
  ) {
    return "";
  }

  return `${dayjs(value[0]).format("MMM D, YYYY")} - ${dayjs(value[1]).format("MMM D, YYYY")}`;
};

export const formatDate = (dateString) => {
  if (
    !dateString ||
    dateString === "" ||
    dateString === null ||
    dateString === undefined
  ) {
    return "";
  }

  try {
    // Handle ISO format strings like "2025-11-16T00:00:00.000+00:00" or "2025-11-16"
    let date;

    if (typeof dateString === "string") {
      // Extract date part (YYYY-MM-DD) from ISO string
      const datePart = dateString.split("T")[0];
      const [year, month, day] = datePart.split("-").map(Number);

      // Create date in local timezone
      date = new Date(year, month - 1, day);
    } else if (dateString instanceof Date) {
      date = dateString;
    } else {
      return "";
    }

    // Validate date
    if (isNaN(date.getTime())) {
      return "";
    }

    // Format as "MMM DD, YYYY" (e.g., "Sep 28, 2025")
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const month = months[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();

    return `${month} ${day}, ${year}`;
  } catch (error) {
    console.error("Error formatting date:", error);
    return "";
  }
};

/**
 * Format an ISO timestamp as "MMM DD, YYYY, h:mm AM/PM" in local time.
 * Date-only strings (no "T") have no time to show and would drift across
 * timezones if parsed via new Date(), so they fall back to date-only.
 * @param {string|Date} dateString - ISO timestamp or Date
 * @returns {string} - Formatted date-time or "" if invalid
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return "";

  if (typeof dateString === "string" && !dateString.includes("T")) {
    return formatDate(dateString);
  }

  try {
    const date =
      dateString instanceof Date ? dateString : new Date(dateString);
    if (isNaN(date.getTime())) return "";

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;

    return `${formatDate(date)}, ${hours}:${minutes} ${ampm}`;
  } catch (error) {
    console.error("Error formatting date-time:", error);
    return "";
  }
};

/**
 * Parse numeric value from currency string
 * Removes currency symbols, commas, and spaces
 * @param {string|number} amount - Currency value to parse
 * @returns {number} - Parsed numeric value or 0 if invalid
 */
export const parseCurrencyAmount = (amount) => {
  if (!amount && amount !== 0) return 0;
  if (typeof amount === "number") return amount;

  const parsed = parseFloat(
    amount
      .toString()
      .replace(/[^\d.-]/g, "")
      .replace(/,/g, "")
  );

  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Validate threshold value
 * @param {string|number} value - Value to validate
 * @returns {Object} - { valid: boolean, error: string|null, numericValue: number|null }
 */
export const validateThresholdValue = (value) => {
  if (value === "" || value === null || value === undefined) {
    return { valid: false, error: "Threshold value is required", numericValue: null };
  }

  const numericValue = typeof value === "string" ? parseFloat(value) : value;

  if (isNaN(numericValue)) {
    return { valid: false, error: "Please enter a valid number", numericValue: null };
  }

  if (numericValue < 0) {
    return { valid: false, error: "Threshold cannot be negative", numericValue: null };
  }

  return { valid: true, error: null, numericValue };
};

/**
 * Build thresholds map for API update
 * @param {Array} accounts - Array of account objects
 * @param {string} targetCurrency - Currency being updated
 * @param {number} newValue - New threshold value
 * @returns {Object} - Map of currency codes to threshold values
 */
export const buildThresholdsMap = (accounts, targetCurrency, newValue) => {
  const thresholdsMap = {};
  accounts.forEach((acc) => {
    const currentValue =
      acc.currency === targetCurrency
        ? newValue
        : parseCurrencyAmount(acc.threshold);
    thresholdsMap[acc.currency] = currentValue;
  });
  return thresholdsMap;
};
